'use client';

import { type FC, useEffect } from 'react';

import { useIsClient } from '../../hooks/use-is-client';
import { useVisibilityChange } from '../../hooks/use-visibility-change';
import { ToastProviderContext } from './context/toast-provider.context';
import { useToastQueue } from './hooks/use-toast-queue';
import type { ToastProviderRootPropsInterface } from './types/toast-provider-root.types';

/**
 * ToastProvider.Root manages the Toast queue and creates context
 * accessible via `useToast()`
 */
export const ToastProviderRoot: FC<ToastProviderRootPropsInterface> = (props) => {
  const {
    children,
    maxSize = 3,
    pauseOnMouseEnter = true,
    pauseOnWindowBlur = true,
    placement = 'bottom-end',
    resumeOnMouseLeave = true,
    resumeOnWindowFocus = true,
    duration = {
      critical: 0,
      warning: 10_000,
      info: 5000,
      success: 5000,
    },
  } = props;

  const { enqueue: enqueueToast, ...queue } = useToastQueue({
    ...props,
    duration,
    placement,
    maxSize,
  });

  const isDocumentVisible = useVisibilityChange();
  const isClient = useIsClient();

  useEffect(() => {
    if (isClient) {
      if (pauseOnWindowBlur && !isDocumentVisible) {
        queue.pauseAll?.();
      }
      if (resumeOnWindowFocus && isDocumentVisible) {
        queue.resumeAll?.();
      }
    }
  }, [pauseOnWindowBlur, queue, resumeOnWindowFocus, isDocumentVisible, isClient]);

  return (
    <ToastProviderContext.Provider
      value={{
        ...queue,
        enqueueToast,
        placement,
        pauseOnMouseEnter,
        resumeOnMouseLeave,
      }}
    >
      {children}
    </ToastProviderContext.Provider>
  );
};

ToastProviderRoot.displayName = 'ToastProvider.Root';
